import React, {useState} from 'react';
import {Layout, Drawer, Tag} from "antd";
import {Button as NButton, Card, Container, Text} from "@nextui-org/react";
import {useCollection} from "react-firebase-hooks/firestore";
import {collection, doc, getFirestore, query, updateDoc, where, serverTimestamp} from "firebase/firestore";
import {getAuth} from "firebase/auth";
import {useToast} from "@chakra-ui/react";
import moment from 'moment';
import Firebase from "../../Firebase/Firebase";
import Headerbar from "../../navBars/Headerbar";
import Sidebar from "../../navBars/Sidebar";
import {logAdminAction} from "../../utils/auditLog";

const db = getFirestore(Firebase);
const auth = getAuth(Firebase);
const {Content} = Layout;

function fmtDate(val: any) {
    if (!val) return '—';
    const d = typeof val.toDate === 'function' ? val.toDate() : val;
    return moment(d).format('DD MMM YYYY, HH:mm');
}

// ── Detail drawer — appeal review ────────────────────────────────────────────

const AppealDrawer = ({appeal, open, onClose}: {appeal: any; open: boolean; onClose: () => void}) => {
    const toast = useToast();
    const [submitting, setSubmitting] = useState(false);

    if (!appeal) return null;

    const resolve = async (decision: 'approved' | 'dismissed') => {
        setSubmitting(true);
        try {
            if (decision === 'approved') {
                await updateDoc(doc(db, 'products', appeal.productId), {productStatus: 'Active'});
            }
            await updateDoc(doc(db, 'productAppeals', appeal.appealId), {
                status: decision,
                resolvedAt: serverTimestamp(),
                resolvedBy: auth.currentUser?.email ?? '',
            });
            await logAdminAction(decision === 'approved' ? 'product_appeal_approved' : 'product_appeal_dismissed', {
                targetId: appeal.productId, targetName: appeal.productName,
                targetType: 'product', from: appeal.previousStatus,
                to: decision === 'approved' ? 'Active' : appeal.previousStatus,
            });
            toast({position: 'top', status: 'success', title: decision === 'approved' ? 'Product reactivated' : 'Appeal dismissed'});
            onClose();
        } catch (e: any) {
            toast({position: 'top', status: 'error', title: 'Failed to resolve appeal', description: e?.message});
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer open={open} onClose={onClose} width={560} title={`Appeal — ${appeal.productName ?? appeal.productId}`}>
            <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>
                <div style={{display: 'flex', gap: 24, flexWrap: 'wrap'}}>
                    <div>
                        <div style={{fontSize: 11, color: '#aaa'}}>SHOP</div>
                        <div style={{fontSize: 13}}>{appeal.sellerName || appeal.sellerId}</div>
                    </div>
                    <div>
                        <div style={{fontSize: 11, color: '#aaa'}}>PREVIOUS STATUS</div>
                        <div style={{fontSize: 13}}>{appeal.previousStatus}</div>
                    </div>
                    <div>
                        <div style={{fontSize: 11, color: '#aaa'}}>SUBMITTED</div>
                        <div style={{fontSize: 13}}>{fmtDate(appeal.createdAt)}</div>
                    </div>
                </div>

                <div style={{background: '#f7f8fc', borderRadius: 8, padding: 16}}>
                    <div style={{fontWeight: 600, marginBottom: 6}}>Seller&apos;s appeal</div>
                    <div style={{fontSize: 13, color: '#333'}}>{appeal.reason || '—'}</div>
                </div>

                {appeal.status !== 'pending' ? (
                    <div style={{background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 16}}>
                        <div style={{fontWeight: 600}}>Already resolved</div>
                        <div style={{fontSize: 13, marginTop: 4}}>
                            {appeal.status === 'approved' ? 'Approved — product reactivated' : 'Dismissed'}
                            {appeal.resolvedBy ? ` by ${appeal.resolvedBy}` : ''}
                        </div>
                    </div>
                ) : (
                    <div style={{display: 'flex', gap: 12}}>
                        <NButton color="error" bordered onClick={() => resolve('dismissed')} disabled={submitting} css={{flex: 1}}>
                            Dismiss
                        </NButton>
                        <NButton color="success" onClick={() => resolve('approved')} disabled={submitting} css={{flex: 1}}>
                            Approve — Reactivate
                        </NButton>
                    </div>
                )}
            </div>
        </Drawer>
    );
};

// ── Main page ──────────────────────────────────────────────────────────────

const Appeals = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [selectedAppeal, setSelectedAppeal] = useState<any>(null);
    const [showResolved, setShowResolved] = useState(false);

    const [snapshot, loading] = useCollection(
        query(collection(db, 'productAppeals'),
            where('status', 'in', showResolved ? ['pending', 'approved', 'dismissed'] : ['pending'])),
    );

    const rows = (snapshot?.docs ?? [])
        .map((d) => ({appealId: d.id, ...d.data()} as any))
        .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));

    return (
        <>
            <Layout style={{minHeight: '100vh'}}>
                <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
                <Layout className="site-layout">
                    <Sidebar collapsed={collapsed}/>
                    <Content>
                        <Container className="mt-2">
                            <Card className="my-2 p-3 font-JosefinSans">
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8}}>
                                    <Text h4 size={23} css={{color: "#29327b", fontWeight: "$bold"}}>
                                        Product Appeals
                                    </Text>
                                    <label style={{fontSize: 13, display: 'flex', alignItems: 'center', gap: 6}}>
                                        <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
                                        Show resolved
                                    </label>
                                </div>

                                {loading ? (
                                    <div style={{padding: 40, textAlign: 'center', color: '#999'}}>Loading…</div>
                                ) : rows.length === 0 ? (
                                    <div style={{padding: 40, textAlign: 'center', color: '#999'}}>
                                        No {showResolved ? '' : 'pending '}appeals.
                                    </div>
                                ) : (
                                    <div style={{marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8}}>
                                        {rows.map((appeal) => (
                                            <div
                                                key={appeal.appealId}
                                                onClick={() => setSelectedAppeal(appeal)}
                                                style={{
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    padding: '12px 16px', border: '1px solid #eee', borderRadius: 8, cursor: 'pointer',
                                                    background: '#fff',
                                                }}
                                            >
                                                <div>
                                                    <div style={{fontWeight: 600, fontSize: 14}}>{appeal.productName ?? appeal.productId}</div>
                                                    <div style={{fontSize: 12, color: '#888'}}>{appeal.sellerName || appeal.sellerId}</div>
                                                </div>
                                                <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                                                    <Tag color={appeal.status === 'pending' ? 'gold' : appeal.status === 'approved' ? 'green' : 'default'}>
                                                        {appeal.status === 'pending' ? 'Pending' : appeal.status === 'approved' ? 'Approved' : 'Dismissed'}
                                                    </Tag>
                                                    <span style={{fontSize: 12, color: '#aaa'}}>{fmtDate(appeal.createdAt)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </Container>
                    </Content>
                </Layout>
            </Layout>

            <AppealDrawer
                appeal={selectedAppeal}
                open={selectedAppeal !== null}
                onClose={() => setSelectedAppeal(null)}
            />
        </>
    );
};

export default Appeals;
